/**
 * The one place a rewrite message is made (UD-32). A fault says its cause and the next step; an outcome that is not a fault
 * (nothing to rewrite) is an information notice with the reason. Nothing wraps a message a second time: the view shows
 * `text` as it is, and `details` holds the original message behind a disclosure.
 */
import type { NothingFacts } from '../../shared/bench-protocol.ts';
import { BENCH, MESSAGES } from '../strings-bench.ts';

export interface BenchMessage {
	/** `info` is an outcome, `error` a fault. */
	kind: 'info' | 'error';
	text: string;
	/** The original message, for the disclosure (faults only). */
	details?: string;
}

export const infoMessage = (text: string): BenchMessage => ({ kind: 'info', text });

export type FaultStep = keyof typeof MESSAGES.lead;

const field = (e: unknown, k: string): unknown =>
	e && typeof e === 'object' ? (e as Record<string, unknown>)[k] : undefined;
const num = (e: unknown, k: string): number | undefined => {
	const v = field(e, k);
	return typeof v === 'number' ? v : undefined;
};
const str = (e: unknown, k: string): string | undefined => {
	const v = field(e, k);
	return typeof v === 'string' ? v : undefined;
};

interface Cause {
	cause: string;
	next: string;
}

/** The cause of a fault, from what a provider error carries (`status`, `type`, `message`) and the worker's own errors. */
export function classifyFault(e: unknown): Cause {
	const message = e instanceof Error ? e.message : String(e);
	const status = num(e, 'status');
	const type = str(e, 'type') ?? '';
	const code = String(status ?? '');
	const F = MESSAGES.fault;

	if (message === 'the bench worker stopped') return F.worker;
	if (message.startsWith('relay refused:')) {
		const relay = message.slice('relay refused:'.length).trim();
		if (relay === 'missing_key') return F.key(code || '401');
		if (relay === 'rate_limited') return F.rateLimit(code || '429');
		if (relay === 'too_large') return F.tooLarge(code || '413');
		if (relay === 'upstream_unreachable') return F.network;
		return F.relay(relay || code);
	}
	if (status === 401 || type === 'authentication_error') return F.key(code || '401');
	if (status === 403 || type === 'permission_error') return F.permission(code || '403');
	if (/credit balance/i.test(message)) return F.credit;
	if (status === 404 || type === 'not_found_error') return F.notFound(code || '404');
	if (status === 413 || type === 'request_too_large') return F.tooLarge(code || '413');
	if (status === 429 || type === 'rate_limit_error') return F.rateLimit(code || '429');
	if ((status !== undefined && status >= 500) || type === 'overloaded_error')
		return F.server(code || '529');
	if (status === 0 || type === 'network_error') return F.network;
	if (/^model not allowed/.test(message)) return F.model(message);
	if (status === 400 || type === 'invalid_request_error') return F.rejected(code || '400', message);
	if (/^(prepare first|nothing was prepared|no run is open|no provider is set)/.test(message))
		return F.lost;
	return F.unknown(message);
}

/** A fault: the step that stopped, its cause and the next step, with the original message in the details. */
export function describeFault(e: unknown, step: FaultStep): BenchMessage {
	const { cause, next } = classifyFault(e);
	const message = e instanceof Error ? e.message : String(e);
	const status = num(e, 'status');
	const type = str(e, 'type');
	const name = e instanceof Error && e.name !== 'Error' ? `${e.name} ` : '';
	const tag = [status !== undefined ? String(status) : '', type ?? ''].filter(Boolean).join(' ');
	return {
		kind: 'error',
		text: `${MESSAGES.lead[step]} ${cause} ${next}`,
		details: `${name}${tag ? `${tag}: ` : ''}${message}`,
	};
}

/** The cause and the next step as one sentence pair, for the messages that carry their own lead ("could not be measured: …"). */
export function faultReason(e: unknown): string {
	const { cause, next } = classifyFault(e);
	return `${cause} ${next}`;
}

/** A stored job error is already worded: the list shows it as it is, behind one lead. */
export const jobStopped = (stored: string): BenchMessage => ({
	kind: 'error',
	text: MESSAGES.jobStopped(stored),
});

/* ---------------- nothing to rewrite ---------------- */

const list = (ids: readonly string[], max = 8): string =>
	ids.length <= max
		? ids.join(', ')
		: `${ids.slice(0, max).join(', ')} and ${ids.length - max} more`;

const place = (n: number): string => (n === 0 ? 'the original' : `version ${n}`);

const actionName = (f: NothingFacts): string => {
	switch (f.scope) {
		case 'flagged':
			return BENCH.rewriteFlagged;
		case 'document':
			return BENCH.rewriteDocument;
		case 'translation':
			return BENCH.repairTranslation;
		case 'channel':
			return BENCH.rewriteChannel;
		case 'hotspot':
			return BENCH.rewriteStretch;
		case 'selection':
			return BENCH.rewriteSelection;
	}
};

/**
 * A task that made no unit: an outcome, with why. `latestN` is the version the task ran on; `viewedN` the version on screen
 * (the views and the rewrite can differ, which is the usual source of surprise).
 */
export function describeNothing(
	f: NothingFacts,
	ctx: { latestN: number; viewedN: number | null }
): BenchMessage {
	const N = MESSAGES.nothing;
	const parts: string[] = [N.lead(actionName(f), place(ctx.latestN))];
	const mismatch = ctx.viewedN !== null && ctx.viewedN !== ctx.latestN;
	if (mismatch) parts.push(N.viewed(place(ctx.viewedN as number), place(ctx.latestN)));

	const fixed = f.firing.filter((c) => c.route === 'N');
	const actionable = f.firing.filter((c) => c.route !== 'N');
	const scored = String(f.scored);
	let reasonsToo = false;
	const n = f.named;
	if (f.scope === 'translation') {
		parts.push(n && n.status !== 'scored' ? N.mtAbstained(n.reason ?? 'abstained') : N.mtNone);
	} else if (n) {
		const fires = f.firing.find((c) => c.id === n.id);
		if (n.status !== 'scored') parts.push(N.channelAbstained(n.id, n.reason ?? 'abstained'));
		else if (n.route === 'N' && n.fired)
			parts.push(N.channelFixed(n.id, fires?.reason ?? 'it is not actionable'));
		else if (!n.fired)
			parts.push(f.scope === 'hotspot' ? N.channelNotHere(n.id) : N.channelNot(n.id));
		else {
			parts.push(N.channelNoUnit(n.id));
			reasonsToo = true;
		}
	} else if (f.scope === 'selection') {
		if (f.items.inScope === 0 && actionable.length === 0) parts.push(N.noSignalsHere(scored));
		else {
			parts.push(N.noUnit(list(actionable.map((c) => c.id))));
			reasonsToo = true;
		}
	} else if (f.firing.length === 0 && f.scored === 0) {
		parts.push(N.noneScored(String(f.abstained)));
	} else if (f.firing.length === 0) {
		parts.push(
			N.noSignals(scored, String(f.documentChannels.fired), String(f.documentChannels.scored))
		);
	} else if (actionable.length === 0) {
		parts.push(
			N.onlyUnrewritable(list(fixed.map((c) => (c.reason ? `${c.id} (${c.reason})` : c.id))))
		);
	} else {
		parts.push(N.noUnit(list(actionable.map((c) => c.id))));
		reasonsToo = true;
	}
	if (reasonsToo && f.reasons.length > 0)
		parts.push(N.because(f.reasons.map(([why, k]) => `${why} (${k})`).join('; ')));

	parts.push(N.sent, mismatch ? N.next.viewed : N.next.plain);
	return infoMessage(parts.join(' '));
}
